import * as React from "react"
import { cn } from "../../lib/utils"
import { formatDecimal, parseMoney } from "../../utils/format"

export interface UnitInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  unit: string
  onValueChange?: (value: number | null) => void
  inputClassName?: string
  unitContainerClassName?: string
  unitTextClassName?: string
}

export const UnitInput = React.forwardRef<HTMLInputElement, UnitInputProps>(
  (
    {
      className,
      unit,
      value,
      onChange,
      onValueChange,
      onBlur,
      inputClassName,
      unitContainerClassName,
      unitTextClassName,
      ...props
    },
    ref
  ) => {
    // Determine initial local string based on incoming number value
    // Using formatDecimal instead of formatMoney to allow variable decimals (e.g. 10 or 10,5)
    const [localValue, setLocalValue] = React.useState<string>(() =>
      formatDecimal(value as number | undefined)
    )

    // Sync from parent if value changes externally
    React.useEffect(() => {
      const currentParsed = parseMoney(localValue);
      const propValue = value === undefined || value === null || value === '' ? undefined : Number(value);

      if (propValue !== undefined && propValue !== currentParsed) {
        setLocalValue(formatDecimal(propValue));
      } else if (propValue === undefined) {
        if (localValue !== '') {
          setLocalValue('');
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value

      // Allow empty
      if (inputValue === '') {
        setLocalValue('')
        onChange?.(e)
        onValueChange?.(null)
        return
      }

      // Formato es-AR estricto:
      // - Punto (.) = separador de miles (opcional, múltiples permitidos)
      // - Coma (,) = separador decimal (solo uno permitido)
      // - Permitir hasta 4 decimales
      // Patrón: dígitos con puntos opcionales para miles, seguido opcionalmente por coma y hasta 4 dígitos
      const typingRegex = /^\d{1,3}(\.\d{3})*(,\d{0,4})?$|^\d+(,\d{0,4})?$/
      
      if (!typingRegex.test(inputValue)) {
        return
      }

      setLocalValue(inputValue)
      onChange?.(e)

      if (onValueChange) {
        const parsed = parseMoney(inputValue)
        onValueChange(parsed)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Si está vacío, dejarlo vacío
      if (localValue === '') {
        onBlur?.(e)
        return
      }

      const parsed = parseMoney(localValue)
      if (parsed !== null) {
        // Formatear el valor parseado correctamente
        const formatted = formatDecimal(parsed)
        setLocalValue(formatted)
      } else {
        // Si no se puede parsear, limpiar el campo
        setLocalValue('')
        onValueChange?.(null)
      }
      onBlur?.(e)
    }

    return (
      <div
        className={cn(
          "flex w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm",
          "focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-0",
          "dark:border-neutral-600 dark:bg-neutral-900",
          className
        )}
      >
        <input
          type="text"
          ref={ref}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={cn(
            "min-w-0 flex-1 px-2 sm:px-4 py-2.5 sm:py-3 text-base sm:text-sm bg-transparent text-right tabular-nums",
            "outline-none focus:outline-none focus:ring-0",
            "disabled:opacity-50 disabled:pointer-events-none",
            "dark:text-neutral-200 dark:placeholder-neutral-500",
            inputClassName
          )}
          autoComplete="off"
          {...props}
        />

        <div
          className={cn(
            "inline-flex shrink-0 items-center px-2 sm:px-4 min-w-fit",
            "border-l border-gray-200 bg-gray-50",
            "dark:border-neutral-600 dark:bg-neutral-700",
            unitContainerClassName
          )}
        >
          <span className={cn("text-xs sm:text-sm text-gray-500 dark:text-neutral-300", unitTextClassName)}>
            {unit}
          </span>
        </div>
      </div>
    )

  }
)
UnitInput.displayName = "UnitInput"
